import { useMutation } from '@apollo/client';
import { CREATE_COST_CODE_GROUP, UPDATE_COST_CODE_GROUP } from 'apollo/mutations/cost-code-group-mutations';
import { COST_CODE_GROUPS_QUERY } from 'apollo/queries/cost-code-group-queries';
import { useApolloPaging } from 'hooks';
import { DateTime } from 'luxon';
import ICostCodeGroup, { ICostCodeGroupCreateInput, ICostCodeGroupUpdateInput } from 'types/CostCodeGroup';
import ICursorable from 'types/Cursorable';
import { getNowIsoAtUtcWithLocalTimeZone } from 'utils/dateUtils';
import uuid from 'uuid';

export default function useCostCodeGroup() {
  const { getAll } = useApolloPaging();
  const [createCostCodeGroupMutation] = useMutation(CREATE_COST_CODE_GROUP);
  const [updateCostCodeGroupMutation] = useMutation(UPDATE_COST_CODE_GROUP);

  async function getCostCodeGroups(groupId: string | null) {
    const results = await getAll<ICostCodeGroup & ICursorable>('costCodeGroups', {
      query: COST_CODE_GROUPS_QUERY,
      variables: {
        first: 500,
        filter: {
          id: groupId ? { equal: groupId } : undefined,
          deletedOn: { isNull: true },
        },
      },
      fetchPolicy: 'network-only',
    });

    return results;
  }

  async function createCostCodeGroup(organizationId: string, groupName: string) {
    const createOnUtc = getNowIsoAtUtcWithLocalTimeZone();
    const group: ICostCodeGroupCreateInput = {
      id: uuid(),
      organizationId,
      groupName,
      createdOn: createOnUtc,
    };
    return new Promise<ICostCodeGroup>((resolve, reject) => {
      return createCostCodeGroupMutation({ variables: { costCodeGroup: group } }).then((result) => {
        const costCodeGroup = result.data?.createCostCodeGroup;
        if (costCodeGroup) {
          resolve(costCodeGroup);
        } else {
          reject();
        }
      });
    });
  }

  async function updateCostCodeGroup(costCodeGroup: ICostCodeGroup, groupName: string | null) {
    const group: ICostCodeGroupUpdateInput = {
      id: costCodeGroup.id,
      groupName,
    };
    return new Promise<ICostCodeGroup>((resolve, reject) => {
      return updateCostCodeGroupMutation({ variables: { costCodeGroup: group } }).then((result) => {
        const costCodeGroup = result.data?.updateCostCodeGroup;
        if (costCodeGroup) {
          resolve(costCodeGroup);
        } else {
          reject();
        }
      });
    });
  }

  async function deleteCostCodeGroup(costCodeGroupId: string) {
    const group: ICostCodeGroupUpdateInput = {
      id: costCodeGroupId,
      deletedOn: DateTime.utc().toISO(),
    };
    return new Promise<ICostCodeGroup>((resolve, reject) => {
      return updateCostCodeGroupMutation({ variables: { costCodeGroup: group } }).then((result) => {
        const costCodeGroup = result.data?.updateCostCodeGroup;
        if (costCodeGroup) {
          resolve(costCodeGroup);
        } else {
          reject();
        }
      });
    });
  }

  return { getCostCodeGroups, createCostCodeGroup, updateCostCodeGroup, deleteCostCodeGroup };
}
